/**
 * Recorder.
 *
 * @copyright Realeyes OU. All rights reserved.
 */


import EventEmitter from 'events';
import RecorderError from './RecorderError';
import ArgumentError from '../utils/ArgumentError';
import MediaStream from './MediaStream';


export default class Recorder extends EventEmitter {
    /**
     * Creates recorder instance.
     *
     * @param {PluginRecordingApi} plugin Recorder plugin instance
     * @param {MediaStream} mediaStream Media stream
     */
    constructor(plugin, mediaStream) {
        super();

        if (!mediaStream || !(mediaStream instanceof MediaStream)) {
            throw new ArgumentError('You should pass a valid MediaStream instance');
        }

        this.originalRecorder = new plugin.Recorder(mediaStream.getPluginStream());
        this.state = 'inactive';
    }

    /**
     * Returns recorder state.
     *
     * @return {string}
     */
    getState() {
        return this.state;
    }

    /**
     * Starts the recorder.
     *
     * @return {Promise.<boolean, RecorderError>}
     */
    start() {
        if (this.state === 'disposed') {
            return this.handleError('Can not start disposed recorder');
        }

        return this.originalRecorder.start()
            .then((result) => {
                this.state = 'recording';
                this.emit('start');

                return result;
            }, err => this.handleError(err));
    }

    /**
     * Stops the recorder.
     *
     * @return {Promise.<boolean, RecorderError>}
     */
    stop() {
        if (this.state === 'disposed') {
            return this.handleError('Can not stop disposed recorder');
        }

        return this.originalRecorder.stop()
            .then(() => {
                this.state = 'inactive';
                this.emit('stop');

                return true;
            }, err => this.handleError(err));
    }

    /**
     * Releases the recorder resources.
     *
     * @return {Promise.<boolean, RecorderError>}
     */
    dispose() {
        return this.originalRecorder.dispose()
            .then(() => {
                this.state = 'disposed';

                return true;
            }, err => this.handleError(err));
    }

    /**
     * Returns the recordings list from the plugin.
     *
     * @return {Promise.<*, RecorderError>}
     */
    getRecordings() {
        if (this.state === 'disposed') {
            return this.handleError('Can not getRecordings from disposed recorder');
        }

        return this.originalRecorder.getRecordings()
            .catch(err => this.handleError(err));
    }

    /**
     * Wraps an error object in a RecorderError, emits the error event and rejects the call.
     *
     * @private
     * @param {*} err Error object.
     * @return {Promise.<*, RecorderError>}
     */
    handleError(err) {
        const recorderError = new RecorderError(err);
        this.emit('error', recorderError);

        return Promise.reject(recorderError);
    }
}
